import { useEffect, useRef, useState, type ReactNode } from "react";
import i18n from "../../../../i18n";
import HeadphonesIcon from "../../../../images/quiz-question/audio-playback-headphones-icon.svg?react";
// Reused as-is from the grading-side session player's control set — pure presentational components
// (no Jotai/apiClient coupling), so this stays pixel-identical instead of hand-mirroring their look.
import {
	PlayButton,
	VolumeControls,
	VideoTimeStamp,
	VideoSpeedButton,
	FullScreenButton,
} from "../../../../results/components/student-selection/videoplayer/VideoControls/VideoControls";
import VideoControlLayout, {
	VideoControlButtonWrapper,
} from "../../../../results/components/student-selection/videoplayer/VideoControls/VideoControlLayout";
import { QuestionAttemptSeekbar } from "../../../../results/components/student-selection/videoplayer/VideoControls/QuestionAttemptSeekbar";
import PlayOverlay from "../../../../results/components/student-selection/videoplayer/overlay/PlayOverlay";
import styles from "./RecordingPlaybackPlayer.module.css";

function t(key: string, opts?: Record<string, unknown>): string {
	return i18n.t(`components.recordingPlaybackPlayerTSX.${key}`, opts) as string;
}

function formatTime(totalSeconds: number): string {
	const clamped = Math.max(0, Math.round(totalSeconds || 0));
	const minutes = Math.floor(clamped / 60);
	const seconds = clamped % 60;
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export type RecordingPlaybackPlayerProps = {
	fileUrl: string;
	mode: "audio" | "video";
	/** Backfills a "total duration" display before the first `timeupdate` — e.g. from a live recording's own timer. */
	onDurationChange?: (durationSec: number) => void;
	/** Overlaid top-right on the media area — e.g. a "Re-recording" pill. */
	cornerAction?: ReactNode;
	audioPosterHint?: string;
};

/**
 * Self-contained audio/video playback UI: seek, play/pause, volume, and (video-only) fullscreen.
 * Always drives a single <video> element — audio-only .webm plays through it fine — so there's one
 * playback API regardless of mode; audio mode just hides the frame behind a poster (hiding a <video>
 * element doesn't stop its audio track from playing). Reused by the take-assessment recording review
 * flow and the grading results view.
 */
export function RecordingPlaybackPlayer({
	fileUrl,
	mode,
	onDurationChange,
	cornerAction,
	audioPosterHint,
}: RecordingPlaybackPlayerProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const frameRef = useRef<HTMLDivElement>(null);
	const controlContainerRef = useRef<HTMLDivElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [hasEnded, setHasEnded] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [volume, setVolume] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [playbackSpeed, setPlaybackSpeed] = useState(1);
	const [speedMenuShowing, setSpeedMenuShowing] = useState(false);

	useEffect(() => {
		const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === frameRef.current);
		document.addEventListener("fullscreenchange", onFullscreenChange);
		return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
	}, []);

	useEffect(() => {
		videoRef.current?.pause();
		videoRef.current?.load();
		setIsPlaying(false);
		setHasEnded(false);
		setCurrentTime(0);
		setDuration(0);
	}, [fileUrl, mode]);

	const handlePlayPause = () => {
		const videoEl = videoRef.current;
		if (!videoEl) return;
		if (videoEl.ended) {
			videoEl.currentTime = 0;
			setCurrentTime(0);
			setHasEnded(false);
		}
		if (videoEl.paused) {
			void videoEl.play();
		} else {
			videoEl.pause();
		}
	};

	/** QuestionAttemptSeekbar's range input works in milliseconds (it also drives full session timelines). */
	const handleSeekMs = (e: React.ChangeEvent<HTMLInputElement>) => {
		const seconds = e.target.valueAsNumber / 1000;
		if (videoRef.current) videoRef.current.currentTime = seconds;
		setCurrentTime(seconds);
		if (hasEnded && seconds < seekableDuration) setHasEnded(false);
	};

	const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.valueAsNumber;
		setVolume(value);
		setIsMuted(value === 0);
		if (videoRef.current) videoRef.current.volume = value;
	};

	const toggleMute = () => {
		const videoEl = videoRef.current;
		if (!videoEl) return;
		const nextMuted = !isMuted;
		setIsMuted(nextMuted);
		videoEl.volume = nextMuted ? 0 : volume || 1;
		if (!nextMuted && volume === 0) setVolume(1);
	};

	const toggleFullscreen = () => {
		if (document.fullscreenElement) {
			void document.exitFullscreen();
		} else {
			void frameRef.current?.requestFullscreen();
		}
	};

	const handleSelectSpeed = (speed: number) => {
		setPlaybackSpeed(speed);
		if (videoRef.current) videoRef.current.playbackRate = speed;
	};

	const seekableDuration = Number.isFinite(duration) ? duration : 0;
	const playLabel = mode === "audio" ? t("playAudioLabel") : undefined;
	const pauseLabel = mode === "audio" ? t("pauseAudioLabel") : undefined;
	const replayLabel = mode === "audio" ? t("replayAudioLabel") : undefined;
	const controlsLabel = mode === "audio" ? t("audioPlaybackControlsLabel") : undefined;
	const mediaAreaClassName =
		mode === "video"
			? isFullscreen
				? `${styles.mediaAreaVideo} ${styles.mediaAreaVideoFullscreen}`
				: styles.mediaAreaVideo
			: isFullscreen
				? `${styles.mediaAreaAudio} ${styles.mediaAreaAudioFullscreen}`
				: styles.mediaAreaAudio;

	return (
		<div className={styles.player} ref={frameRef}>
			<div className={mediaAreaClassName}>
				<video
					ref={videoRef}
					src={fileUrl}
					className={mode === "video" ? styles.video : styles.videoHiddenForAudio}
					playsInline
					onPlay={() => setIsPlaying(true)}
					onPause={() => setIsPlaying(false)}
					onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
					onLoadedMetadata={(e) => {
						setDuration(e.currentTarget.duration);
						onDurationChange?.(e.currentTarget.duration);
					}}
					onEnded={() => {
						setIsPlaying(false);
						setHasEnded(true);
					}}
				/>
				{mode === "audio" && (
					<div className={styles.audioPoster}>
						<HeadphonesIcon aria-hidden="true" />
						<p className={styles.audioPosterHint}>{audioPosterHint ?? t("audioPosterHint")}</p>
					</div>
				)}
				<PlayOverlay
					useVideoEndedOverlay={hasEnded}
					isOverlayVisible={!isPlaying}
					setPlay={handlePlayPause}
					playLabel={playLabel}
					restartLabel={replayLabel}
					circleClassName={styles.playOverlayCircleCompact}
				/>
				{cornerAction && <div className={styles.cornerAction}>{cornerAction}</div>}
			</div>

			<div className={isFullscreen ? `${styles.controlsBox} ${styles.controlsBoxFullscreen}` : styles.controlsBox}>
				<QuestionAttemptSeekbar
					events={[]}
					curTimeMs={currentTime * 1000}
					durationMs={seekableDuration > 0 ? seekableDuration * 1000 : undefined}
					timeOffsetMs={0}
					onSeekbarChange={handleSeekMs}
					inFullscreen={isFullscreen}
					isPlaying={isPlaying}
				/>

				<VideoControlLayout
					inFullscreen={isFullscreen}
					controlContainerRef={controlContainerRef}
					ariaLabel={controlsLabel}
					className={styles.controlLayoutCompact}
				>
					<PlayButton
						isPlaying={isPlaying}
						onClick={handlePlayPause}
						inFullscreen={isFullscreen}
						playLabel={playLabel}
						pauseLabel={pauseLabel}
					/>
					<VolumeControls
						inFullscreen={isFullscreen}
						volume={isMuted ? 0 : volume}
						onVolumeButtonClick={toggleMute}
						onVolumeInputChange={handleVolumeChange}
						showVolumeInput
					/>
					<VideoTimeStamp inFullscreen={isFullscreen}>
						{formatTime(currentTime)}
						<span style={{ color: "#FFFFFF99" }}> / {formatTime(seekableDuration)}</span>
					</VideoTimeStamp>

					<VideoControlButtonWrapper>
						<VideoSpeedButton
							inFullscreen={isFullscreen}
							menuShowing={speedMenuShowing}
							setMenuShowing={setSpeedMenuShowing}
							playbackSpeed={playbackSpeed}
							controlContainerRef={controlContainerRef}
							onSelectSpeed={handleSelectSpeed}
						/>
						{mode === "video" && <FullScreenButton inFullscreen={isFullscreen} onClick={toggleFullscreen} />}
					</VideoControlButtonWrapper>
				</VideoControlLayout>
			</div>
		</div>
	);
}

export default RecordingPlaybackPlayer;
